import Decimal from 'decimal.js';

// What a case has cost, from the usage its services record against it (planning-case-service's record-usage): every model
// call priced by the model it ran on, and the premium OS map tiles. Bedrock bills in US dollars and OS in pounds, so the
// two are kept apart.

export interface UsageLine {
  at: string;
  phase: string;
  step: string;
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  /** USD; null when the model was not in the price table. */
  costUsd: number | null;
  /** Not paid for, and why; costUsd is then what it would have cost. */
  notCharged?: NotCharged;
  /** The service that bills it; Bedrock (model calls) when absent. */
  service?: string;
}

/** The services a case can be charged by, other than the OS map (billed in pounds, kept apart). */
export const SERVICES = ['Bedrock', 'Amazon Location', 'Textract'] as const;

export type NotCharged = 'Mock' | 'Dev' | 'Cache';

/** Why a cost was not incurred, in words. */
export const NOT_CHARGED_LABEL: Record<NotCharged, string> = {
  Mock: 'Mock mode',
  Dev: 'Dev mode (answered by hand)',
  Cache: 'Answered from a cache (already paid for)',
};

export interface CostRow {
  service: string;
  phase: string;
  step: string;
  model: string;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  /** USD for the priced calls in this row. */
  costUsd: number;
  /** Calls on a model with no price: their tokens are counted, their cost is not known. */
  unpricedCalls: number;
  /** Set when these calls were not paid for; costUsd is then the estimate. */
  notCharged?: NotCharged;
}

export interface CaseCosts {
  rows: CostRow[];
  /** What was actually paid for model calls. */
  modelUsd: number;
  byPhaseUsd: Record<string, number>;
  /** What calls not paid for (mock, Dev mode, a reused result) would have cost. */
  notChargedUsd: number;
  notChargedByPhaseUsd: Record<string, number>;
  /** USD paid, and what was not, by service (Bedrock, Amazon Location, Textract). */
  byServiceUsd: Record<string, number>;
  notChargedByServiceUsd: Record<string, number>;
  unpricedCalls: number;
  osMapTiles: number;
  osMapGbp: number;
  /** Premium tiles served from the locate service's memory, and what they would have cost. */
  osMapCachedTiles: number;
  osMapCachedGbp: number;
  /** Planning tokens charged for runs on the case, and given back for failed ones. */
  tokensSpent: number;
  tokensRefunded: number;
  /** Nothing has been metered: a case from before metering, or one with no model calls or premium tiles yet. */
  empty: boolean;
}

export function parseUsageLines(json: string | null | undefined): UsageLine[] {
  if (!json) return [];
  try {
    const v = JSON.parse(json) as unknown;
    return Array.isArray(v) ? v as UsageLine[] : [];
  } catch {
    return [];
  }
}

/** Lines folded into one row per phase, step and model, in the order the phases first happened. */
export function summariseCosts(
  lines: UsageLine[], osMapTiles?: number | null, osMapCostGbp?: number | null, osMapCachedTiles?: number | null, osMapCachedCostGbp?: number | null,
  tokensSpent?: number | null, tokensRefunded?: number | null,
): CaseCosts {
  const rows = new Map<string, CostRow & { usd: Decimal }>();
  const byPhase = new Map<string, Decimal>();
  const notChargedByPhase = new Map<string, Decimal>();
  const byService = new Map<string, Decimal>();
  const notChargedByService = new Map<string, Decimal>();
  const add = (m: Map<string, Decimal>, k: string, v: number) => m.set(k, (m.get(k) ?? new Decimal(0)).plus(v));
  let total = new Decimal(0);
  let notChargedTotal = new Decimal(0);
  let unpriced = 0;
  for (const l of lines) {
    const service = l.service ?? 'Bedrock';
    const key = `${service}|${l.phase}|${l.step}|${l.model}|${l.notCharged ?? ''}`;
    let row = rows.get(key);
    if (!row) {
      row = {
        service, phase: l.phase, step: l.step, model: l.model, calls: 0, inputTokens: 0, outputTokens: 0, costUsd: 0, unpricedCalls: 0, usd: new Decimal(0),
        ...(l.notCharged ? { notCharged: l.notCharged } : {}),
      };
      rows.set(key, row);
    }
    row.calls += l.calls;
    row.inputTokens += l.inputTokens;
    row.outputTokens += l.outputTokens;
    if (l.costUsd == null) {
      row.unpricedCalls += l.calls;
      unpriced += l.calls;
    } else if (l.notCharged) {
      row.usd = row.usd.plus(l.costUsd);
      add(notChargedByPhase, l.phase, l.costUsd);
      add(notChargedByService, service, l.costUsd);
      notChargedTotal = notChargedTotal.plus(l.costUsd);
    } else {
      row.usd = row.usd.plus(l.costUsd);
      add(byPhase, l.phase, l.costUsd);
      add(byService, service, l.costUsd);
      total = total.plus(l.costUsd);
    }
  }
  const tiles = osMapTiles ?? 0;
  const cachedTiles = osMapCachedTiles ?? 0;
  const asNumbers = (m: Map<string, Decimal>) => Object.fromEntries([...m].map(([k, v]) => [k, v.toNumber()]));
  return {
    rows: [...rows.values()].map(({ usd, ...r }) => ({ ...r, costUsd: usd.toNumber() })),
    modelUsd: total.toNumber(),
    byPhaseUsd: asNumbers(byPhase),
    notChargedUsd: notChargedTotal.toNumber(),
    notChargedByPhaseUsd: asNumbers(notChargedByPhase),
    byServiceUsd: asNumbers(byService),
    notChargedByServiceUsd: asNumbers(notChargedByService),
    unpricedCalls: unpriced,
    osMapTiles: tiles,
    osMapGbp: new Decimal(osMapCostGbp ?? 0).toNumber(),
    osMapCachedTiles: cachedTiles,
    osMapCachedGbp: new Decimal(osMapCachedCostGbp ?? 0).toNumber(),
    tokensSpent: tokensSpent ?? 0,
    tokensRefunded: tokensRefunded ?? 0,
    empty: lines.length === 0 && tiles === 0 && cachedTiles === 0 && !tokensSpent,
  };
}

export function fmtUsd(n: number): string {
  return `$${n > 0 && n < 0.01 ? n.toFixed(4) : n.toFixed(2)}`;
}

export function fmtGbp(n: number): string {
  return `£${n > 0 && n < 0.01 ? n.toFixed(4) : n.toFixed(2)}`;
}
